import {
    useState,
    useEffect,
    useId,
    useCallback,
    type ReactNode,
} from "react";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { ChangelogModal } from "./ChangelogModal";

const MOBILE_LAYOUT_MEDIA = "(max-width: 768px)";
/** Matches `App.scss` - side-by-side row before column stack */
const DESKTOP_SPLIT_MEDIA = "(min-width: 901px)";
const CONTROL_PANEL_EXPANDED_KEY = "control-panel-expanded";
const APP_NAME = "SVGexp";

function readControlPanelExpanded(): boolean {
    try {
        const v = localStorage.getItem(CONTROL_PANEL_EXPANDED_KEY);
        if (v === null) return true;
        return v === "1";
    } catch {
        return true;
    }
}

export interface AppPanelLayoutProps {
    viewer: ReactNode;
    controls: ReactNode;
}

export function AppPanelLayout({ viewer, controls }: AppPanelLayoutProps) {
    const isMobileLayout = useMediaQuery(MOBILE_LAYOUT_MEDIA);
    const isWideSplit = useMediaQuery(DESKTOP_SPLIT_MEDIA);
    const [controlsOpen, setControlsOpen] = useState(false);
    const [controlPanelExpanded, setControlPanelExpanded] = useState(
        readControlPanelExpanded,
    );
    const dialogId = useId();
    const changelogDialogId = useId();
    const [changelogOpen, setChangelogOpen] = useState(false);

    const toggleControlPanel = useCallback(() => {
        setControlPanelExpanded((prev) => {
            const next = !prev;
            try {
                localStorage.setItem(
                    CONTROL_PANEL_EXPANDED_KEY,
                    next ? "1" : "0",
                );
            } catch {
                /* ignore */
            }
            return next;
        });
    }, []);

    useEffect(() => {
        if (!isMobileLayout) setControlsOpen(false);
    }, [isMobileLayout]);

    useEffect(() => {
        if (!controlsOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== "Escape") return;
            if (changelogOpen) return;
            setControlsOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [controlsOpen, changelogOpen]);

    const desktopLayoutClass = [
        "app-layout",
        isWideSplit && !controlPanelExpanded
            ? "app-layout--control-collapsed"
            : "",
    ]
        .filter(Boolean)
        .join(" ");

    const versionFooter = (
        <div className="control-panel-footer">
            <span className="control-panel-footer-version">
                {APP_NAME} v{__APP_VERSION__}
            </span>
            <button
                type="button"
                className="control-panel-changelog-btn"
                onClick={() => setChangelogOpen((o) => !o)}
                aria-expanded={changelogOpen}
                aria-haspopup="dialog"
                aria-controls={changelogDialogId}
                title={
                    changelogOpen ? "Close changelog" : "View changelog"
                }
            >
                Changelog
            </button>
        </div>
    );

    return (
        <>
            {!isMobileLayout ? (
                <div className={desktopLayoutClass}>
                    <div className="viewer-panel">{viewer}</div>
                    <div className="control-panel">
                        {isWideSplit && (
                            <button
                                type="button"
                                className="control-panel-toggle"
                                onClick={toggleControlPanel}
                                aria-expanded={controlPanelExpanded}
                                title={
                                    controlPanelExpanded
                                        ? "Collapse controls"
                                        : "Expand controls"
                                }
                            >
                                <svg
                                    className="control-panel-toggle-chevron"
                                    viewBox="0 0 24 24"
                                    width="18"
                                    height="18"
                                    aria-hidden
                                >
                                    {controlPanelExpanded ? (
                                        <path
                                            d="M10 6l6 6-6 6"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    ) : (
                                        <path
                                            d="M14 6l-6 6 6 6"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    )}
                                </svg>
                                {controlPanelExpanded && (
                                    <span className="control-panel-toggle-text">
                                        Controls
                                    </span>
                                )}
                            </button>
                        )}
                        <div className="control-panel-body">{controls}</div>
                        {versionFooter}
                    </div>
                </div>
            ) : (
                <div className="app-layout app-layout--mobile">
                    <div className="viewer-panel">{viewer}</div>
                    <button
                        type="button"
                        className="panel-mobile-controls-btn"
                        onClick={() => setControlsOpen(true)}
                        aria-expanded={controlsOpen}
                        aria-haspopup="dialog"
                        aria-controls={dialogId}
                        title="Controls"
                    >
                        <svg
                            className="panel-mobile-controls-btn-icon"
                            viewBox="0 0 24 24"
                            width="22"
                            height="22"
                            aria-hidden
                        >
                            <path
                                d="M4 6h16M7 12h10M5 18h14"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                            />
                        </svg>
                        <span className="panel-mobile-controls-btn-text">
                            Controls
                        </span>
                    </button>
                    {controlsOpen && (
                        <>
                            <div
                                className="panel-mobile-controls-backdrop"
                                aria-hidden
                                onClick={() => setControlsOpen(false)}
                            />
                            <div
                                id={dialogId}
                                className="panel-mobile-controls-popover"
                                role="dialog"
                                aria-modal="true"
                                aria-label="Controls"
                            >
                                <div className="panel-mobile-popover-header">
                                    <span className="panel-mobile-popover-title">
                                        Controls
                                    </span>
                                    <button
                                        type="button"
                                        className="panel-mobile-popover-close"
                                        onClick={() =>
                                            setControlsOpen(false)
                                        }
                                        aria-label="Close controls"
                                    >
                                        x
                                    </button>
                                </div>
                                <div className="panel-mobile-controls-popover-body">
                                    <div className="control-panel control-panel--popover">
                                        <div className="control-panel-body">
                                            {controls}
                                        </div>
                                        {versionFooter}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}
            <ChangelogModal
                open={changelogOpen}
                onClose={() => setChangelogOpen(false)}
                dialogId={changelogDialogId}
            />
        </>
    );
}
